/**
 * Time shift parsing utilities for converting human-readable time strings to milliseconds
 * and vice versa. Supports various formats like "5s", "1h30m", "2d", "1y", etc.
 */

/**
 * Parse a human-readable time delta string into milliseconds
 * 
 * Supported formats:
 * - Pure numbers: "5" → 5000ms (assumes seconds)
 * - Single units: "5s", "2m", "1h", "3d", "1y"
 * - Combined units: "1h30m", "2d5h", "1y6m2d"
 * - Negative values: "-5s", "-1h30m"
 * - HH:MM:SS format: "01:30:45"
 * - Decimal values: "2.5s", "1.5h"
 * 
 * @param input - The time string to parse
 * @returns Time in milliseconds, or 0 for invalid/empty input
 */
export function parseDeltaTime(input: string): number {
  console.log(`🔍 Parsing time delta: "${input}"`)
  
  if (!input.trim()) {
    console.log(`🔍 Empty input, returning 0`)
    return 0
  }
  
  // Remove whitespace and convert to lowercase
  const cleaned = input.trim().toLowerCase()
  console.log(`🔍 Cleaned input: "${cleaned}"`)
  
  // Handle pure numbers (assume seconds)
  const numericValue = parseFloat(cleaned)
  if (!isNaN(numericValue) && /^[+-]?\d*\.?\d+$/.test(cleaned)) {
    const result = numericValue * 1000
    console.log(`🔍 Pure number detected: ${numericValue}s = ${result}ms`)
    return result
  }
  
  // Parse time units: 1y2d1h30m15s, -5m, +10s, etc.
  let totalMs = 0
  const isNegative = cleaned.startsWith('-')
  
  // Remove leading +/- for parsing
  const timeStr = cleaned.replace(/^[+-]/, '')
  
  // Extract years, days, hours, minutes, seconds
  const yearMatch = timeStr.match(/(\d*\.?\d+)y/)
  const dayMatch = timeStr.match(/(\d*\.?\d+)d/)
  const hourMatch = timeStr.match(/(\d*\.?\d+)h/)
  const minuteMatch = timeStr.match(/(\d*\.?\d+)m/)
  const secondMatch = timeStr.match(/(\d*\.?\d+)s/)
  
  if (yearMatch) {
    const years = parseFloat(yearMatch[1])
    totalMs += years * 365.25 * 24 * 60 * 60 * 1000 // Account for leap years
    console.log(`🔍 Found ${years} years = ${years * 365.25 * 24 * 60 * 60 * 1000}ms`)
  }
  if (dayMatch) {
    const days = parseFloat(dayMatch[1])
    totalMs += days * 24 * 60 * 60 * 1000
    console.log(`🔍 Found ${days} days = ${days * 24 * 60 * 60 * 1000}ms`)
  }
  if (hourMatch) {
    const hours = parseFloat(hourMatch[1])
    totalMs += hours * 60 * 60 * 1000
    console.log(`🔍 Found ${hours} hours = ${hours * 60 * 60 * 1000}ms`)
  }
  if (minuteMatch) {
    const minutes = parseFloat(minuteMatch[1])
    totalMs += minutes * 60 * 1000
    console.log(`🔍 Found ${minutes} minutes = ${minutes * 60 * 1000}ms`)
  }
  if (secondMatch) {
    const seconds = parseFloat(secondMatch[1])
    totalMs += seconds * 1000
    console.log(`🔍 Found ${seconds} seconds = ${seconds * 1000}ms`)
  }
  
  // If no units found, try HH:MM:SS format
  if (totalMs === 0) {
    const timePattern = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/
    const match = timeStr.match(timePattern)
    if (match) {
      const part1 = parseInt(match[1]) || 0
      const part2 = parseInt(match[2]) || 0
      const part3 = parseInt(match[3]) || 0
      
      if (match[3]) {
        // HH:MM:SS format
        totalMs = (part1 * 60 * 60 + part2 * 60 + part3) * 1000
        console.log(`🔍 Found HH:MM:SS format: ${part1}:${part2}:${part3} = ${totalMs}ms`)
      } else {
        // MM:SS format (no hours)
        totalMs = (part1 * 60 + part2) * 1000
        console.log(`🔍 Found MM:SS format: ${part1}:${part2} = ${totalMs}ms`)
      }
    }
  }
  
  // If still no match found, return 0 (graceful degradation)
  if (totalMs === 0 && timeStr !== '0') {
    console.log(`🔍 No valid time format found in "${input}", returning 0`)
    return 0
  }
  
  const finalResult = isNegative ? -totalMs : totalMs
  console.log(`🔍 Final parsed result: ${finalResult}ms (${finalResult/1000}s)`)
  return finalResult
}

/**
 * Format milliseconds back into a human-readable time delta string
 * 
 * @param offsetMs - Time offset in milliseconds
 * @returns Formatted string like "5s", "1h30m", "2d", etc.
 */
export function formatDeltaTime(offsetMs: number): string {
  if (offsetMs === 0) return ''
  
  const isNegative = offsetMs < 0
  const absMs = Math.abs(offsetMs)
  const totalSeconds = absMs / 1000
  
  // For clean numbers under a minute, show in seconds
  if (totalSeconds < 60 && totalSeconds % 1 === 0) {
    return `${isNegative ? '-' : ''}${totalSeconds}s`
  }
  
  // For longer durations, use mixed format with years
  const yearInSeconds = 365.25 * 24 * 3600
  const years = Math.floor(totalSeconds / yearInSeconds)
  const remainingAfterYears = totalSeconds - (years * yearInSeconds)
  
  const days = Math.floor(remainingAfterYears / (24 * 3600))
  const remainingAfterDays = remainingAfterYears - (days * 24 * 3600)
  
  const hours = Math.floor(remainingAfterDays / 3600)
  const remainingAfterHours = remainingAfterDays - (hours * 3600)
  
  const minutes = Math.floor(remainingAfterHours / 60)
  const seconds = remainingAfterHours - (minutes * 60)
  
  // Build result in canonical order (y d h m s) with spaces between units
  const parts: string[] = []
  if (years > 0) parts.push(`${years}y`)
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0) parts.push(`${minutes}m`)
  // Only include seconds if they're significant (> 0.001)
  if (seconds > 0.001) parts.push(`${seconds % 1 === 0 ? Math.round(seconds) : seconds.toFixed(1)}s`)
  
  let result = parts.join(' ')  // Add spaces between units for readability
  if (isNegative) result = '-' + result
  
  return result || '0s'
}
